import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../../auth/authActions";
import { clone } from "ramda";
import { notBlank } from "../../util/ext";
import { useSearchFilter } from "../../common/use-searchFilter";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";

import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import TotalsHeader from "../../creator-application/common/TotalsHeader";
import ContentIngestionJobTable from "./ContentIngestionJobTable";
import CadieEventSearchBar from "./CadieEventSearchBar";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },
  }));

const ContentIngestionJobBrowse = ({
  searchState,
  onSearchStateChange,
  searchResults,
}) => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const classes = useStyles(scrollVisible)();

  const [page, setPage] = useState(0);
  const [metadataFileDiscoveries, setMetadataFileDiscoveries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // //-- Handle Search state
  const initialState = clone(searchState);
  const { state, handleUpdateDateRange, handleUpdateSearch } = useSearchFilter(
    initialState,
  );

  //-- Whenever the internal search state changes, update the search state stored in the parent
  useEffect(() => {
    onSearchStateChange(state);
    setScrollVisible(true);
  }, [onSearchStateChange, state]);

  useEffect(() => {
    if (searchResults.isError && searchResults.error.status === 401) {
      dispatch(logOut());
    }

    if (searchResults.isSuccess) {
      setScrollVisible(true);
      setMetadataFileDiscoveries(searchResults.data);
      setTotalCount(searchResults.data.length);
    }
  }, [
    dispatch,
    auth,
    searchResults.data,
    searchResults.isError,
    searchResults.error,
    searchResults.isSuccess,
  ]);

  const handleSearch = useCallback(
    search => {
      if (search.searchBy === "DATE") {
        handleUpdateSearch("filename", null);
        handleUpdateSearch("status", null);
        handleUpdateDateRange(search.startDate, search.endDate);
      } else if (search.searchBy === "FILENAME") {
        handleUpdateDateRange(null, null);
        handleUpdateSearch("status", null);
        handleUpdateSearch("filename", search.filename.trim());
      } else if (search.searchBy === "STATUS") {
        handleUpdateDateRange(null, null);
        handleUpdateSearch("filename", null);
        handleUpdateSearch("status", search.status);
      }

      setPage(0);
    },
    [handleUpdateDateRange, handleUpdateSearch],
  );

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  return (
    <Container id="subscription-browse-view" classes={{ root: classes.root }}>
      <Box>
        <CadieEventSearchBar searchState={state} onSearch={handleSearch} />

        <TotalsHeader countText="Item" count={totalCount} />

        {searchResults.isLoading && <PanelLoadingIndicator />}
        {searchResults.isSuccess && (
          <Container classes={{ root: classes.panel }}>
            {notBlank(metadataFileDiscoveries) ? (
              <ContentIngestionJobTable
                metadataFileDiscoveryList={metadataFileDiscoveries}
                page={page}
                onPageChange={handlePageChange}
                onScrollVisible={handleScrollVisible}
              />
            ) : (
              <Box
                height="calc(100vh - 400px)"
                padding={5}
                fontSize="1.6rem"
                fontStyle="italic"
              >
                No Metadata Discovery Events found matching given criteria.
              </Box>
            )}
          </Container>
        )}
      </Box>
    </Container>
  );
};

export default ContentIngestionJobBrowse;
